// src/services/progressService.js
/**
 * Progress Service — Web API
 *
 * v2 (Decoupled Architecture):
 * Progress and evidence are reviewed independently.
 *
 * REMOVED in v2:
 *   - ProgressStatus.EVIDENCE_REJECTED
 *   - rejectProgress() — use rejectEvidence() in evidenceWebService.js instead
 *
 * Backend: MilestoneProgressController.java
 * Base paths: /api/progress, /api/milestones/{id}/progress, /api/segments/{id}/progress
 */

import api from '../config/axios';

const BASE       = '/progress';
const MILESTONES = '/milestones';
const SEGMENTS   = '/segments';

// ============================================================================
// QUERY OPERATIONS
// ============================================================================

/**
 * GET /api/progress/{id}
 * Full progress detail including evidence list (decoupled — evidence from milestone FK).
 */
export const getProgressById = async (id) => {
  const res = await api.get(`${BASE}/${id}`);
  return res.data;
};

/**
 * GET /api/milestones/{milestoneId}/progress
 * Progress history for a milestone (all submissions, newest first).
 */
export const getProgressByMilestone = async (milestoneId) => {
  const res = await api.get(`${MILESTONES}/${milestoneId}/progress`);
  return res.data;
};

/**
 * GET /api/progress/pending
 * All SUBMITTED + RESUBMITTED submissions awaiting approval.
 */
export const getPendingApprovals = async () => {
  const res = await api.get(`${BASE}/pending`);
  return res.data;
};

/**
 * GET /api/segments/{segmentId}/progress/pending
 */
export const getPendingApprovalsBySegment = async (segmentId) => {
  const res = await api.get(`${SEGMENTS}/${segmentId}/progress/pending`);
  return res.data;
};

/**
 * GET /api/progress/all
 * All submissions (all statuses) — for monitoring dashboard.
 */
export const getAllSubmissions = async () => {
  const res = await api.get(`${BASE}/all`);
  return res.data;
};

/**
 * GET /api/segments/{segmentId}/progress/all
 * All submissions for a specific segment — used by FieldSubmissionDetail.
 */
export const getAllSubmissionsBySegment = async (segmentId) => {
  const res = await api.get(`${SEGMENTS}/${segmentId}/progress/all`);
  return res.data;
};

/**
 * GET /api/users/{userId}/progress
 */
export const getProgressByUser = async (userId) => {
  const res = await api.get(`/api/users/${userId}/progress`);
  return res.data;
};

// ============================================================================
// COMMAND OPERATIONS
// ============================================================================

/**
 * POST /api/milestones/{milestoneId}/progress
 */
export const submitProgress = async (milestoneId, progressData) => {
  const res = await api.post(
    `${MILESTONES}/${milestoneId}/progress`,
    { ...progressData, milestoneId }
  );
  return res.data;
};

/**
 * PUT /api/progress/{progressId}/approve
 *
 * Accept as-is:
 *   { approveAsIs: true, approvalNotes: '...', evidenceApprovals: [...] }
 *
 * Override:
 *   { approveAsIs: false, adjustedProgress: 28.0, adjustmentReason: '...', evidenceApprovals: [...] }
 *
 * evidenceApprovals[] is optional — evidence can also be reviewed
 * independently via evidenceWebService.js
 */
export const approveProgress = async (progressId, payload) => {
  const res = await api.put(`${BASE}/${progressId}/approve`, {
    ...payload,
    progressId,
  });
  return res.data;
};

/**
 * PUT /api/progress/{progressId}/reject
 *
 * Full rejection only — use only for fundamentally wrong submissions.
 * For evidence issues: use evidenceWebService.rejectEvidence() instead.
 *
 * { rejectType: 'FULL_REJECT', rejectionReason: '...' }
 */
export const rejectProgressFull = async (progressId, rejectionReason) => {
  const res = await api.put(`${BASE}/${progressId}/reject`, {
    progressId,
    rejectType: 'FULL_REJECT',
    rejectionReason,
  });
  return res.data;
};

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

/**
 * Progress Status Enum (v2 — EVIDENCE_REJECTED removed)
 */
export const ProgressStatus = {
  DRAFT:       'DRAFT',
  SUBMITTED:   'SUBMITTED',
  RESUBMITTED: 'RESUBMITTED',
  APPROVED:    'APPROVED',
  REJECTED:    'REJECTED',
  // EVIDENCE_REJECTED removed in v2
};

/** Statuses that require PM/PC action */
export const PENDING_STATUSES = [
  ProgressStatus.SUBMITTED,
  ProgressStatus.RESUBMITTED,
];

/** Statuses that require supervisor action */
export const NEEDS_SUPERVISOR_ACTION = [
  ProgressStatus.REJECTED,
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getStatusDisplayName = (status) => ({
  DRAFT:       'Draft',
  SUBMITTED:   'Pending Review',
  RESUBMITTED: 'Resubmitted',
  APPROVED:    'Disetujui',
  REJECTED:    'Ditolak',
}[status] ?? status);

export const getStatusBadgeClass = (status) => ({
  DRAFT:       'bg-gray-100 text-gray-700 border-gray-200',
  SUBMITTED:   'bg-amber-100 text-amber-800 border-amber-200',
  RESUBMITTED: 'bg-blue-100 text-blue-800 border-blue-200',
  APPROVED:    'bg-emerald-100 text-emerald-800 border-emerald-200',
  REJECTED:    'bg-red-100 text-red-800 border-red-200',
}[status] ?? 'bg-gray-100 text-gray-700 border-gray-200');

export const getStatusDotColor = (status) => ({
  SUBMITTED:   'bg-amber-400',
  RESUBMITTED: 'bg-blue-400',
  APPROVED:    'bg-emerald-400',
  REJECTED:    'bg-red-400',
  DRAFT:       'bg-gray-300',
}[status] ?? 'bg-gray-300');

export const getPriorityBadgeClass = (priority) => ({
  HIGH:   'bg-red-100 text-red-800 border-red-200',
  MEDIUM: 'bg-amber-100 text-amber-800 border-amber-200',
  LOW:    'bg-gray-100 text-gray-600 border-gray-200',
}[priority] ?? 'bg-gray-100 text-gray-600 border-gray-200');

export const canReviewProgress = (status) =>
  PENDING_STATUSES.includes(status);

export const formatDate = (iso) => {
  if (!iso) return '-';
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(new Date(iso));
};

export const formatDateTime = (iso) => {
  if (!iso) return '-';
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
};

export const calculateDaysPending = (submittedAt) => {
  if (!submittedAt) return 0;
  return Math.ceil(Math.abs(new Date() - new Date(submittedAt)) / (1000 * 60 * 60 * 24));
};

/**
 * Group a flat list of progress submissions by segment/site.
 * Returns: Map<scopeKey, { scopeType, scopeId, scopeName, scopeCode, projectName, submissions }>
 */
export const groupSubmissionsByScope = (submissions) => {
  const groups = new Map();

  submissions.forEach((sub) => {
    const key = `${sub.scopeType}_${sub.scopeId}`;
    if (!groups.has(key)) {
      groups.set(key, {
        scopeType:   sub.scopeType,
        scopeId:     sub.scopeId,
        scopeName:   sub.scopeName,
        scopeCode:   sub.scopeCode,
        projectName: sub.projectName,
        submissions: [],
      });
    }
    groups.get(key).submissions.push(sub);
  });

  return groups;
};

/**
 * Group submissions within a scope by milestone.
 * Returns: Map<milestoneId, { milestoneId, milestoneName, milestoneItemCode, submissions }>
 */
export const groupByMilestone = (submissions) => {
  const groups = new Map();

  submissions.forEach((sub) => {
    if (!groups.has(sub.milestoneId)) {
      groups.set(sub.milestoneId, {
        milestoneId:       sub.milestoneId,
        milestoneName:     sub.milestoneName,
        milestoneItemCode: sub.milestoneItemCode,
        submissions: [],
      });
    }
    groups.get(sub.milestoneId).submissions.push(sub);
  });

  return groups;
};

/**
 * Compute badge summary for a scope group.
 * Returns: { pendingProgress, approvedProgress, rejectedProgress, evidencePending }
 */
export const computeScopeSummary = (submissions) => ({
  pendingProgress:  submissions.filter(s => PENDING_STATUSES.includes(s.status)).length,
  approvedProgress: submissions.filter(s => s.status === ProgressStatus.APPROVED).length,
  rejectedProgress: submissions.filter(s => s.status === ProgressStatus.REJECTED).length,
  evidencePending:  submissions.reduce((acc, s) => acc + (s.evidencePendingCount ?? 0), 0),
});

export default {
  getProgressById,
  getProgressByMilestone,
  getPendingApprovals,
  getPendingApprovalsBySegment,
  getAllSubmissions,
  getAllSubmissionsBySegment,
  getProgressByUser,
  submitProgress,
  approveProgress,
  rejectProgressFull,
  ProgressStatus,
  PENDING_STATUSES,
  getStatusDisplayName,
  getStatusBadgeClass,
  getStatusDotColor,
  getPriorityBadgeClass,
  canReviewProgress,
  formatDate,
  formatDateTime,
  calculateDaysPending,
  groupSubmissionsByScope,
  groupByMilestone,
  computeScopeSummary,
};